<script setup>
import { ref, computed, watch } from 'vue'
import { useNotification } from '@kyvg/vue3-notification'

import AudienceService from '@/services/AudienceService'
import { useAudienceConsole } from '@/components/audience/audienceConsole'

import Alert from '@/components/Atoms/Alert.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import FormSwitch from '@/components/Atoms/FormSwitch.vue'
import ModalBox from '@/components/modals/ModalBox.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconDownload from '@/components/icon/IconDownload.vue'
import IconClock from '@/components/icon/IconClock.vue'

import AudienceChatModeration from '@/components/audience/AudienceChatModeration.vue'
import AudienceQuestions from '@/components/audience/AudienceQuestions.vue'
import AudiencePolls from '@/components/audience/AudiencePolls.vue'
import AudienceBans from '@/components/audience/AudienceBans.vue'
import AudienceArchive from '@/components/audience/AudienceArchive.vue'
import AudienceCtaControl from '@/components/audience/AudienceCtaControl.vue'

// The moderation console: what a host works from mid-broadcast. Everything
// long-lived — the socket, the stats poll — is the shell's; this pane owns
// only what dies gracefully with it: the active pane, the modals, the
// in-flight flags of its own actions.

const { notify } = useNotification()

const props = defineProps({
	stream: {
		type: Object,
		required: true,
		validator(v) {
			return Object.prototype.hasOwnProperty.call(v, '_id')
		}
	},
	streamAlive: {
		type: Boolean
	}
})

const {
	settings,
	usingCastr,
	roomStatus,
	roomError,
	isLive,
	me,
	canSendChat,
	canSendQuestion,
	sendError,
	messages,
	sortedQuestions,
	polls,
	pinned,
	reactionCounts,
	unansweredCount,
	livePollCount,
	connectRoom,
	purgeAndRejoin,
	sendChat,
	sendQuestion,
	stats,
	roomConfig,
	filterSaving,
	setFilter,
	refreshStats,
	banCount,
	activeCta,
	ctaClicks,
	onCtaSet,
	onCtaRemoved
} = useAudienceConsole()

const activeTab = ref('chat')

const purgeModalVisible = ref(false)
const clearChatModalVisible = ref(false)
const bansModalVisible = ref(false)
const archiveModalVisible = ref(false)
const clearingChat = ref(false)

const statusLabel = computed(() => {
	switch (roomStatus.value) {
		case 'live': return 'Connected'
		case 'connecting': return 'Connecting…'
		case 'reconnecting': return 'Reconnecting…'
		case 'failed': return 'Disconnected'
		default: return 'Not connected'
	}
})

const totalReactions = computed(() => {
	return Object.values(reactionCounts.value).reduce((sum, n) => sum + n, 0)
})

const statChips = computed(() => [
	{ value: stats.value ? stats.value.messages : '—', label: 'messages' },
	{ value: stats.value ? stats.value.questions : '—', label: 'questions' },
	{ value: totalReactions.value || (stats.value ? stats.value.reactions : '—'), label: 'reactions' }
])

const selfVid = computed(() => (me.value && me.value.vid) || '')

const roomTabs = computed(() => {
	const tabs = []
	if (settings.value.chat) tabs.push({ key: 'chat', label: 'Chat', count: 0, hint: '' })
	if (settings.value.qa) tabs.push({ key: 'qa', label: 'Q&A', count: unansweredCount.value, hint: 'unanswered' })
	if (settings.value.polls) tabs.push({ key: 'polls', label: 'Polls', count: livePollCount.value, hint: 'live' })
	return tabs
})

/**
 * Why a composer is greyed out, phrased as the thing to do about it. Only
 * called when posting is off, so the last branch is the awkward one: the room
 * accepted us before this feature was switched on, and the token we joined
 * with does not carry it.
 */
const blockedReason = (feature) => {
	if (!settings.value[feature]) {
		return feature === 'qa'
			? 'Q&A is switched off for this stream.'
			: 'Chat is switched off for this stream.'
	}
	if (roomStatus.value === 'connecting') return 'Connecting to the room…'
	if (roomStatus.value === 'reconnecting') return 'Reconnecting to the room…'
	if (!isLive.value) return 'Connect to the room to post.'
	return 'Rejoining the room to pick this up…'
}

/** The pane-scoped half of a rejection; room-wide ones show as an Alert. */
const errorFor = (origin) => {
	const err = sendError.value
	return err && err.origin === origin ? err.message : ''
}

const roomLevelError = computed(() => {
	const err = sendError.value
	return err && !err.origin ? err.message : ''
})

// The rejection a host is most likely to be able to fix themselves, and the one
// whose message ("hosts included") sends them looking for a switch. Putting the
// switch next to the complaint saves the hunt.
const linkBlockHit = computed(() => {
	const err = sendError.value
	return !!err && err.code === 'link_blocked' && !!(roomConfig.value && roomConfig.value.blockLinks)
})

const downloadExport = async () => {
	try {
		const data = await AudienceService.getExport(props.stream._id)
		const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
		const url = URL.createObjectURL(blob)

		const link = document.createElement('a')
		link.href = url
		link.download = `audience-${props.stream.key || props.stream._id}.json`
		link.click()
		URL.revokeObjectURL(url)
	} catch (err) {
		notify({ group: 'error', text: err.message || 'Could not export the room' })
	}
}

/**
 * Chat only. Nobody is disconnected and nothing else in the room is touched —
 * the room broadcasts `chat.clear` and every client, this one included, empties
 * the list it is rendering.
 */
const confirmClearChat = async () => {
	clearingChat.value = true

	try {
		const res = await AudienceService.clearMessages(props.stream._id)
		const count = (res && res.count) || 0

		clearChatModalVisible.value = false
		notify({
			group: 'success',
			text: count
				? `Chat cleared, ${count} message${count === 1 ? '' : 's'} removed`
				: 'Chat cleared, there was nothing to remove'
		})

		// Messages are the only counter this moved, but the snapshot is cheap and
		// keeping the tiles honest matters more than the request.
		refreshStats()
	} catch (err) {
		notify({ group: 'error', text: err.message || 'Could not clear the chat' })
	}

	clearingChat.value = false
}

const confirmPurge = async () => {
	purgeModalVisible.value = false

	// Blanked up front: whichever way the purge goes, the counts on screen stop
	// being true the moment we ask for it. The shell's isLive watcher pulls the
	// zeroed snapshot once the rejoin lands.
	stats.value = null

	try {
		// Rejoining is part of clearing: the worker closes our socket as it
		// wipes the room, and the composable sequences the way back in.
		await purgeAndRejoin(() => AudienceService.purgeRoom(props.stream._id))
	} catch (err) {
		notify({ group: 'error', text: err.message || 'Could not clear the room' })
		return
	}

	notify({ group: 'success', text: 'Room cleared. Archived results are still available.' })
}

watch(roomTabs, (tabs) => {
	if (tabs.length && !tabs.some((t) => t.key === activeTab.value)) {
		activeTab.value = tabs[0].key
	}
}, { immediate: true })

/** Left/right arrows move between panes, which is what a tablist owes anyone not using a mouse. */
const onTabKeydown = (event, index) => {
	const tabs = roomTabs.value
	if (tabs.length < 2) return

	let next = null
	if (event.key === 'ArrowRight') next = (index + 1) % tabs.length
	if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length
	if (next === null) return

	event.preventDefault()
	activeTab.value = tabs[next].key

	const buttons = event.currentTarget.parentElement.querySelectorAll('[role="tab"]')
	if (buttons[next]) buttons[next].focus()
}
</script>

<template>
	<!-- The room needs the Castr provider; in every other state the pane says
	so briefly rather than disappearing, because the sub-tab is still there. -->
	<section v-if="!usingCastr" class="bg-surface-1 rounded-xl p-4 md:p-5">
		<p class="text-sm text-surface-text-1 mb-1">The live room is not available.</p>
		<p class="text-tiny text-surface-7 mb-0">
			Chat is switched off. Turn it on with the switch above and the moderation console —
			chat, Q&amp;A, polls, and the room controls — opens here.
		</p>
	</section>

	<section v-else class="bg-surface-1 rounded-xl p-4 md:p-5">
		<div class="flex flex-wrap items-center justify-between gap-3 mb-4">
			<div class="flex items-center gap-x-3">
				<h3 class="text-xl font-medium mb-0">Live room</h3>
				<span class="inline-flex items-center gap-x-1.5 text-tiny text-surface-7">
					<span class="w-2 h-2 rounded-full"
						:class="isLive ? 'bg-success' : 'bg-surface-6'"></span>
					{{ statusLabel }}
				</span>
			</div>

			<div class="flex flex-wrap items-center gap-2">
				<FormButton v-if="!isLive" type="secondary" size="sm"
					:is-loading="roomStatus === 'connecting'" @click="connectRoom">
					Connect
				</FormButton>
				<FormButton type="secondary" size="sm"
					title="Poll and Q&A results kept after the room was cleared"
					@click="archiveModalVisible = true">
					<IconBase class="!mr-1"><IconClock /></IconBase>
					Archive
				</FormButton>
				<FormButton type="secondary" size="sm" title="Download the current room as JSON"
					@click="downloadExport">
					<IconBase class="!mr-1"><IconDownload /></IconBase>
					Export
				</FormButton>

				<!-- The two clear actions are one word apart, so they are
				kept off to their own side of a divider rather than sitting
				in the run of ordinary buttons. -->
				<span class="w-px h-6 bg-surface-3 mx-1" aria-hidden="true"></span>

				<FormButton type="secondary" size="sm"
					title="Delete chat messages only. Q&A, polls, bans and viewers all stay."
					@click="clearChatModalVisible = true">
					Clear chat
				</FormButton>
				<!-- Red at rest, not only on hover: one word is all that
				separates this from the harmless clear beside it. Styled
				through the fallthrough class, NOT custom-classes, which
				FormButton treats as a wholesale replacement and which
				left this as a bare unpadded text button. -->
				<FormButton type="danger" size="sm"
					class="!text-error-light !ring-error-light hover:!bg-surface-2 disabled:!text-surface-7 disabled:!ring-surface-5"
					title="Delete everything in the room and disconnect every viewer."
					@click="purgeModalVisible = true">
					Clear room
				</FormButton>
			</div>
		</div>

		<Alert v-if="roomStatus === 'failed'" type="error" class="mb-4"
			:text="roomError || 'Could not reach the chat room.'" />

		<!-- A rejection we could not pin on either composer — a ban
		landing, or an error arriving long after anything we sent. -->
		<Alert v-if="roomLevelError" type="error" class="mb-4" :text="roomLevelError" />

		<Alert v-if="linkBlockHit" type="warning" class="mb-4">
			<span class="flex flex-wrap items-center gap-x-2 gap-y-1">
				This room blocks links for everyone, hosts included.
				<button type="button" class="underline font-medium"
					:disabled="filterSaving === 'blockLinks'" @click="setFilter('blockLinks', false)">
					Allow links
				</button>
			</span>
		</Alert>

		<!-- One glanceable line, not four tall tiles: these are context,
		and every pixel they give back goes to the message list. -->
		<div class="flex flex-wrap items-center gap-2 mb-3">
			<span v-for="stat in statChips" :key="stat.label"
				class="inline-flex items-baseline gap-x-1.5 bg-surface-2 rounded-md px-2.5 py-1">
				<span class="text-base font-semibold leading-5 text-surface-text-2 tabular-nums">{{ stat.value }}</span>
				<span class="text-tiny text-surface-7">{{ stat.label }}</span>
			</span>
		</div>

		<!-- The pinned call-to-action. Directly under the numbers rather
		than among the filters: while one is live it is the single most
		visible thing the host is doing to every viewer's panel. -->
		<AudienceCtaControl class="mb-3" :stream-id="stream._id" :cta="activeCta"
			:clicks="ctaClicks" @set="onCtaSet" @removed="onCtaRemoved" />

		<!-- Everything a moderator reaches for without leaving the room:
		the two content filters, which are room state rather than stream
		settings, and the bans they have already handed out. Captured
		emails live on their own sub-tab, next to where capture is set up. -->
		<div class="flex flex-wrap items-center gap-x-5 gap-y-2 bg-surface-2 rounded-lg px-3 py-2.5 mb-4">
			<FormSwitch v-if="roomConfig" v-model="roomConfig.profanityFilter" size="sm"
				label="Profanity filter" label-position="left"
				label-styles="text-sm text-surface-text-1"
				:disabled="filterSaving === 'profanityFilter'"
				@change="setFilter('profanityFilter', roomConfig.profanityFilter)" />
			<FormSwitch v-if="roomConfig" v-model="roomConfig.blockLinks" size="sm"
				label="Block links" label-position="left"
				label-styles="text-sm text-surface-text-1"
				:disabled="filterSaving === 'blockLinks'"
				@change="setFilter('blockLinks', roomConfig.blockLinks)" />
			<span v-if="!roomConfig" class="text-sm text-surface-7">
				Content filters load with the room.
			</span>

			<button type="button"
				class="text-sm text-surface-8 hover:text-white underline underline-offset-2 ml-auto"
				@click="bansModalVisible = true">
				Banned viewers<span v-if="banCount" class="font-medium"> · {{ banCount }}</span>
			</button>
		</div>

		<div v-if="roomTabs.length > 1" role="tablist" aria-label="Room content"
			class="inline-flex items-center gap-1 bg-surface-2 rounded-lg p-1 mb-3">
			<button v-for="(tab, index) in roomTabs" :key="tab.key" type="button" role="tab"
				:aria-selected="activeTab === tab.key" :tabindex="activeTab === tab.key ? 0 : -1"
				class="inline-flex items-center text-sm font-medium px-3 py-1.5 rounded-md"
				:class="activeTab === tab.key
					? 'bg-surface-4 text-white shadow-sm'
					: 'text-surface-8 hover:text-white'"
				:title="tab.count ? `${tab.count} ${tab.hint}` : null"
				@keydown="onTabKeydown($event, index)" @click="activeTab = tab.key">
				{{ tab.label }}
				<!-- Unanswered questions / polls taking votes: where a
				host's attention is owed without cycling the panes. -->
				<span v-if="tab.count"
					class="ml-1.5 inline-flex items-center justify-center min-w-[1.1rem] rounded-full bg-surface-1 text-main text-tiny leading-4 px-1">
					{{ tab.count }}
				</span>
			</button>
		</div>

		<div class="h-[420px] max-h-[70vh]">
			<AudienceChatModeration v-if="activeTab === 'chat' && settings.chat"
				:stream-id="stream._id" :messages="messages" :pinned="pinned"
				:connected="isLive" :self-vid="selfVid" :can-post="canSendChat"
				:blocked-reason="blockedReason('chat')" :send-error="errorFor('chat')"
				:show-country-flags="settings.countryFlags" :on-send="sendChat" @banned="refreshStats" />
			<AudienceQuestions v-else-if="activeTab === 'qa' && settings.qa"
				:stream-id="stream._id" :questions="sortedQuestions"
				:connected="isLive" :self-vid="selfVid" :can-post="canSendQuestion"
				:blocked-reason="blockedReason('qa')" :send-error="errorFor('qa')"
				:on-send="sendQuestion" />
			<AudiencePolls v-else-if="activeTab === 'polls' && settings.polls"
				:stream-id="stream._id" :polls="polls" :connected="isLive" />
		</div>

		<!-- Two clear actions that read alike from the toolbar have to read
		nothing alike here, so each one leads with what survives it. -->
		<ModalBox v-model="clearChatModalVisible" title="Clear chat" size="sm" scroll-body>
			<div class="px-4 pb-4 text-sm text-surface-9 sm:px-6 sm:pb-6">
				<p class="text-surface-text-1 mb-3">
					Deletes the messages in chat and nothing else. Questions, polls, votes, bans and your
					room settings are all left as they are, and nobody is disconnected.
				</p>
				<p class="mb-0">
					Everyone watching sees their chat empty where they are. Deleted messages cannot be
					brought back.
				</p>
			</div>
			<template #footer>
				<footer class="flex flex-col gap-2 border-t border-surface-3 p-4 sm:flex-row-reverse sm:p-6">
					<FormButton class="w-full justify-center sm:w-auto" :is-loading="clearingChat"
						@click="confirmClearChat">
						Clear chat
					</FormButton>
					<FormButton class="w-full justify-center sm:w-auto" type="secondary"
						@click="clearChatModalVisible = false">
						Cancel
					</FormButton>
				</footer>
			</template>
		</ModalBox>

		<ModalBox v-model="purgeModalVisible" title="Clear the whole room" size="sm" scroll-body>
			<div class="px-4 pb-4 text-sm text-surface-9 sm:px-6 sm:pb-6">
				<p class="text-surface-text-1 mb-3">
					Deletes every message, question and poll for this stream, and disconnects everyone
					currently watching. Their panel stays gone until they reload the page.
				</p>
				<p class="mb-3">
					Poll results that have already been archived stay available under Archive. Anything
					else is gone for good, so export first if you need it.
				</p>
				<p class="mb-0">
					To empty chat without disrupting the room, use Clear chat instead.
				</p>
			</div>
			<template #footer>
				<footer class="flex flex-col gap-2 border-t border-surface-3 p-4 sm:flex-row-reverse sm:p-6">
					<FormButton class="w-full justify-center sm:w-auto" type="danger" @click="confirmPurge">
						Clear room
					</FormButton>
					<FormButton class="w-full justify-center sm:w-auto" type="secondary"
						@click="purgeModalVisible = false">
						Cancel
					</FormButton>
				</footer>
			</template>
		</ModalBox>

		<AudienceBans v-model="bansModalVisible" :stream-id="stream._id" @changed="refreshStats" />
		<AudienceArchive v-model="archiveModalVisible" :stream-id="stream._id" />
	</section>
</template>
